import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { setStoredAdmin } from "../../utils/auth";
import { BASE_URL } from "../../config";
import { Hotel, AlertCircle, ArrowRight, Eye, EyeOff } from "lucide-react";

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (!formData.username || !formData.password) {
      setError("Username and password are required");
      return;
    }

    setLoading(true);

    try {
      const endpoint = `${BASE_URL}/admin/login`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
       setError(data.message || "Incorrect username or password. Please try again.");
       setLoading(false);
        return;
      }

      // Store admin data from backend response
      setStoredAdmin(data.admin);
      navigate("/dashboard");
    } catch (err: any) {
      setError("Unable to connect to server. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-brand-700 flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-500/20 via-transparent to-brand-800/40" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-warm-500/8 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-brand-400/10 rounded-full blur-3xl" />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
              <img 
              src="/La-La.png"
              alt="LOGO"
              className="w-12 h-12 object-contain rounded-lg"
              />
            <span className="text-xl font-bold text-white">LA-LA</span>
          </div>
          <p className="text-sm text-brand-200 ml-[52px]">Administration Panel</p>
        </div>

        <div className="relative z-10">
          <h1 className="text-4xl font-serif font-bold text-white leading-tight text-balance">
            Manage your hotel portfolio with confidence
          </h1>
          <p className="text-brand-200 mt-4 text-lg leading-relaxed max-w-md">
            Monitor listings, approve registrations, and track your hospitality business across Tanzania.
          </p>
        </div>

        <div className="relative z-10 flex items-center gap-6">
          <div>
            <p className="text-2xl font-bold text-white">500+</p>
            <p className="text-sm text-brand-300">Hotels Managed</p>
          </div>
          <div className="w-px h-10 bg-brand-500" />
          <div>
            <p className="text-2xl font-bold text-white">12K+</p>
            <p className="text-sm text-brand-300">Active Guests</p>
          </div>
          <div className="w-px h-10 bg-brand-500" />
          <div>
            <p className="text-2xl font-bold text-white">98%</p>
            <p className="text-sm text-brand-300">Satisfaction</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-beige-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-brand-500 flex items-center justify-center">
              <img 
              src="/LA-LA.png"
              alt="LOGO"
              className="w-10 h-10 object-contain rounded-lg"
              />
            </div>
            <span className="text-xl font-bold text-sage-900">LA-LA</span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-serif font-bold text-sage-900">Welcome back</h2>
            <p className="text-sage-500 mt-1.5">Sign in to your admin account to continue</p>
          </div>

          {error && (
            <div className="mb-6 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-sage-700 mb-1.5">
                Username
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 bg-white border border-beige-300 rounded-xl text-sage-900 placeholder-sage-400 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-colors"
                placeholder="Enter your username"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-sage-700">
                  Password
                </label>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 pr-11 bg-white border border-beige-300 rounded-xl text-sage-900 placeholder-sage-400 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-colors"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sage-400 hover:text-sage-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:bg-brand-300 text-white font-semibold text-sm rounded-xl transition-colors shadow-sm"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Sign in
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          <p className="text-center text-xs text-sage-400 mt-8">
            LA-LA Administration Panel
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
